import React, { useContext, useEffect, useState } from "react";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { PaymentData } from "../../entry/server/app.ts";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";
import { Link } from "react-router-dom";

function getStatusMeta(status: string) {
  const normalized = (status || "").toLowerCase();

  if (normalized === "pending") {
    return { label: "درحال انتظار", className: "bg-sky-500/20 text-sky-300" };
  }

  if (normalized === "approved" || normalized === "accepted" || normalized === "paid") {
    return { label: "تأیید شده", className: "bg-emerald-500/20 text-emerald-300" };
  }

  if (normalized === "rejected" || normalized === "declined" || normalized === "failed") {
    return { label: "رد شده", className: "bg-rose-500/20 text-rose-300" };
  }

  return { label: status || "-", className: "bg-slate-700/60 text-slate-300" };
}

export default function TransactionsScreen() {
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const { setMessage } = useContext(AppContext);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await appRepo.getPayments(page);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست تراکنش‌ها ناموفق بود." });
        setPayments([]);
        return;
      }
      setPayments(response.data);
      setPagination(response.pagination ?? null);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onApprove = async (id: number) => {
    if (actionLoading !== null) return;
    setActionLoading(id);
    try {
      const res = await appRepo.approvePayment(id);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "تأیید تراکنش ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "تراکنش با موفقیت تأیید شد." });
      await fetchPayments();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setActionLoading(null);
    }
  };

  const onReject = async (id: number) => {
    if (actionLoading !== null) return;
    if (!window.confirm("این تراکنش رد شود؟")) return;

    setActionLoading(id);
    try {
      const res = await appRepo.rejectPayment(id);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "رد تراکنش ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "تراکنش با موفقیت رد شد." });
      await fetchPayments();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <section className="mt-4 space-y-4">
        <AdminPageTitle title="تراکنش‌ها" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست تراکنش‌ها...
        </div>
      </section>
    );
  }

  return (
    <section className="mt-4 space-y-4">
      <AdminPageTitle title="تراکنش‌ها" />

      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <table className="min-w-full divide-y divide-slate-800 text-sm">
          <thead className="bg-slate-900/80">
            <tr>
              <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">کاربر</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">پلن</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">درگاه</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">مبلغ</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">کد تخفیف</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">وضعیت</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">تاریخ</th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {payments.map((p) => {
              const statusMeta = getStatusMeta(p.status);
              const isPending = (p.status || "").toLowerCase() === "pending";

              return (
                <tr key={p.id} className="transition hover:bg-slate-900">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300">{p.id}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-blue-500"><Link to={`/admin/users/${p.user_id}`}>{p.username || "—"}</Link></td>
                  <td className="whitespace-nowrap px-4 py-3 text-blue-500"><Link to={`/admin/plans/${p.plan_id}/edit`}>{p.plan_name || "—"}</Link></td>
                  <td className="whitespace-nowrap px-4 py-3 text-blue-500"><Link to={`/admin/gateways/${p.gateway_id}/edit`}>{p.gateway_name || "—"}</Link></td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-200">{p.amount ?? 0}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300">{p.discount_code || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}>
                      {statusMeta.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-300">{p.created_at || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={!isPending || actionLoading === p.id}
                        onClick={() => void onApprove(p.id)}
                        className="rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        تأیید
                      </button>
                      <button
                        type="button"
                        disabled={!isPending || actionLoading === p.id}
                        onClick={() => void onReject(p.id)}
                        className="rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        رد
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!payments.length && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-sm text-slate-400">
                  تراکنشی یافت نشد.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
    </section>
  );
}

